// --- App Version ---
export const APP_VERSION = 0.9;

// --- Simulation Time ---
export interface SimTime {
	hour: number;
	minute: number;
	second: number;
}

// --- Waypoint with optional stop ---
export interface Waypoint {
	position: [number, number]; // [lat, lng]
	stopDuration: number; // seconds to dwell at this point (0 = no stop)
	speed?: number; // m/s (optional, falls back to agent's global speed)
}

// --- Segment: a leg of a journey with its own time config ---
export interface Segment {
	timeMode: "start" | "end";
	startTime: SimTime;
	endTime: SimTime;
	waypoints: Waypoint[];
}

// --- Agent types ---
export type AgentType = "person" | "car" | "police" | "ambulance";

// Default emojis per type (for non-person agents)
export const AGENT_EMOJIS: Record<AgentType, string> = {
	person: "👫",
	car: "🚗",
	police: "🚓",
	ambulance: "🚑",
};

export const AGENT_TYPE_LABELS: Record<AgentType, string> = {
	person: "Person",
	car: "Bil",
	police: "Polis",
	ambulance: "Ambulans",
};

// Available SVG icons for person agents
export const PERSON_SVG_OPTIONS = [
	{ value: "male.svg", label: "Man" },
	{ value: "female.svg", label: "Kvinna" },
	{ value: "couple-mw.svg", label: "Par" },
] as const;

// --- Source ---
export interface Source {
	id: string;
	name: string; // short description (max 20 chars)
	url: string; // URL (max 100 chars)
}

// --- Agent ---
export interface Agent {
	id: string;
	type: AgentType;
	label: string; // short label on map (e.g. "LOP")
	name: string; // long form (e.g. "Lisbeth and Olof Palme")
	track: string; // track description (300 chars)
	testimony: string; // testimony description (500 chars)
	speed: number; // m/s
	segments: Segment[];
	color: string;
	emoji: string; // emoji shown on map (for non-person agents)
	svg?: string; // SVG filename for person agents (e.g., "male.svg")
	removeOnEnd?: boolean; // remove agent from canvas when path ends
	sources?: Source[]; // source references with URLs
}

// --- Clock Annotation ---
export interface ClockAnnotation {
	id: string;
	time: SimTime;
	text: string; // supports line breaks
}

// --- Speed Control (dynamic speed changes during playback) ---
export interface SpeedControl {
	id: string;
	time: SimTime;
	speedMultiplier: number; // 1, 2, 5, etc. (default is 2 if no controls defined)
}

// --- Viewport Pin (camera position at specific time) ---
export interface ViewportPin {
	id: string;
	time: SimTime | "default-start"; // "default-start" for first pin linked to scenario startTime
	position: [number, number]; // [lat, lng]
	zoom: 16 | 18 | 20; // small/medium/max
}

// --- Analysis Section (for scenario analysis) ---
export interface AnalysisSection {
	id: string;
	time: SimTime;
	order: number; // manual ordering (0-based)
	headline: string; // section headline
	content: string; // max 600 chars
}

// --- Analysis (optional scenario analysis) ---
export interface Analysis {
	scenarioName: string; // auto-filled from meta.name
	ingress?: string; // optional introduction text
	sections: AnalysisSection[];
}

// --- Scenario Metadata ---
export interface ScenarioMeta {
	version: number; // e.g., 0.90 (for future migrations)
	name: string; // "Default Scenario" or custom name
	startTime: SimTime; // When clock starts
	crimeTime: SimTime; // Where events converge (for audio sync)
	soundEffects: boolean; // Timeline audio on/off (ambient always on)
	endTime?: SimTime; // Optional end time for simulation
	showAnalysisAtEnd?: boolean; // Show analysis modal when endTime is reached
	darkMode?: boolean; // Dark map tiles (optional, defaults to false)
	slug?: string; // Only present if shared (e.g., "abc123def")
	ownerUUID?: string; // UUID of creator (for ownership check)
	createdAt?: string; // ISO timestamp when first created
	updatedAt?: string; // ISO timestamp when last modified
	exportedAt?: string; // ISO timestamp when exported to file
}

// --- Map ---
export interface MapData {
	bounds: [[number, number], [number, number]]; // [[south, west], [north, east]]
	streets: GeoJSON.FeatureCollection;
	buildings: GeoJSON.FeatureCollection;
}

// --- Clock ---
export interface SimClock {
	currentTime: SimTime;
	speedMultiplier: number;
	isRunning: boolean;
}

// --- Project (what gets saved to localStorage) ---
export interface Project {
	meta: ScenarioMeta;
	agents: Agent[];
	clockAnnotations: ClockAnnotation[];
	speedControls?: SpeedControl[]; // optional for backwards compatibility
	analysis?: Analysis; // optional for backwards compatibility
	viewportPins: ViewportPin[]; // always includes at least default-start pin
	autoPan?: boolean; // optional, defaults to true when viewportPins.length > 1
}

// --- App modes ---
export type AppMode = "edit" | "simulate";

export type ScenarioMode = "default" | "custom";

// --- Helpers ---
export function simTimeToSeconds(t: SimTime): number {
	return t.hour * 3600 + t.minute * 60 + t.second;
}

export function secondsToSimTime(s: number): SimTime {
	const clamped = Math.max(0, Math.min(s, 86399));
	const hour = Math.floor(clamped / 3600) % 24;
	const minute = Math.floor((clamped % 3600) / 60);
	const second = Math.floor(clamped % 60);
	return { hour, minute, second };
}

export function formatSimTime(t: SimTime): string {
	return `${String(t.hour).padStart(2, "0")}:${String(t.minute).padStart(2, "0")}:${String(t.second).padStart(2, "0")}`;
}

export function formatSpeed(speedMs: number): string {
	return `${(speedMs * 3.6).toFixed(1)} km/h`;
}

